import { useEffect, useRef } from "react";
import { useApi } from "@akina/infra";
import { useQuery } from "@tanstack/react-query";
import type { WebshopTransactionStatusEnum } from "types/schemas";
import { QueryKeys } from "./query";

const FINAL_PAYMENT_STATUSES: Set<WebshopTransactionStatusEnum> = new Set(["paid", "failed", "canceled", "expired"]);
const MAX_REFETCHES = 3;
const POLL_INTERVAL_MS = 5000;

export function usePollRegistrationStatus(token: string | null) {
  const { api } = useApi();
  const pollCountRef = useRef(0);

  useEffect(() => {
    pollCountRef.current = 0;
  }, [token]);

  const query = useQuery({
    queryKey: [QueryKeys.webshopRegistrationStatus, token],
    queryFn: async () => {
      const result = await api.webshop.getRegistrationStatus(token ?? "");
      pollCountRef.current += 1;
      return {
        ...result,
        hasExceededPollLimit: pollCountRef.current >= MAX_REFETCHES && !FINAL_PAYMENT_STATUSES.has(result.paymentStatus),
      };
    },
    enabled: !!token,
    refetchInterval: query => {
      if (FINAL_PAYMENT_STATUSES.has(query.state.data?.paymentStatus ?? "open")) return false;
      if (pollCountRef.current >= MAX_REFETCHES) return false;
      return POLL_INTERVAL_MS;
    },
    retry: false,
  });

  return {
    ...query,
    hasExceededPollLimit: query.data?.hasExceededPollLimit ?? false,
  };
}
